import { useMutation, useQuery } from '@tanstack/react-query';

import ReservationService from '~/services/ReservationService';

export interface RentalImportRow {
  row_number: number;
  vendor_id?: string;
  offer_id?: string;
  hotel_code?: string;
  errors?: Array<string>;
  import_status?: 'format_error' | 'database_error' | 'queued' | 'importing' | 'imported' | 'failed_import';
}

export interface RentalImportCommitResult {
  batch_id: string;
  state: 'accepted' | 'failed';
  total_rows: number;
  valid_count: number;
  error_count: number;
  validation_results: Array<RentalImportRow>;
}

export type RentalImportState = 'accepted' | 'processing' | 'completed' | 'failed';

export interface RentalImportStatusItem {
  batch_id: string;
  state: RentalImportState;
  total_rows: number;
  processed_rows: number;
  success_count: number;
  error_count: number;
  valid_rows_count: number;
  created_at: string;
  results: Array<RentalImportRow>;
}

export function useCommitRentalImport() {
  return useMutation({
    mutationFn: ({
      file,
      commission,
    }: {
      file: File;
      commission: number;
    }): Promise<{ result: RentalImportCommitResult }> => ReservationService.commitBulkRentalImport(file, commission),
  });
}

export function useRentalImportStatus(batchId: string | null) {
  return useQuery({
    queryKey: ['vendors', 'rental-import', batchId],
    queryFn: (): Promise<{ result: RentalImportStatusItem }> => ReservationService.getBulkRentalImportStatus(batchId),
    enabled: !!batchId,
    retry: false,
    refetchInterval: (query) => {
      if (query.state.error) {
        return false;
      }
      const state = query.state.data?.result.state;
      return state === 'completed' || state === 'failed' ? false : 1200;
    },
  });
}

export function useRetryRentalImportRow() {
  return useMutation({
    mutationFn: ({
      batchId,
      rowNumber,
    }: {
      batchId: string;
      rowNumber: number;
    }): Promise<{ result: RentalImportStatusItem }> => ReservationService.retryBulkRentalImportRow(batchId, rowNumber),
  });
}
